//@flow

import React, { useState, memo } from 'react';
import Slider from 'react-slick';
import { camelizeKeys } from 'humps';
import sortBy from 'lodash/sortBy';

import { api } from '@dom/utils';
import { filter } from '../../utils';
import { SkeletonCard, WhitePanelLayout } from '../../screens/Home/imports';

import './cardSlider.scss';

type GalleryArrowProps = {
  onClick: () => void,
  direction: string,
  name: string,
};

function GalleryArrow(props: GalleryArrowProps) {
  const { onClick, direction, name } = props;

  return (
    <div className={`control ${direction}`}>
      <div aria-label={name} role="button" className="action" onClick={onClick}>
        <i className={`icon icon-hg-chev-${direction}`} />
      </div>
    </div>
  );
}

const settings = toShow => ({
  infinite: false,
  slidesToShow: toShow,
  slidesToScroll: 1,
  autoplay: false,
  swipe: true,
  dots: false,
  lazyLoad: true,
  nextArrow: <GalleryArrow direction="right" name="Right Arrow" />,
  prevArrow: <GalleryArrow direction="left" name="Left Arrow" />,
  responsive: [
    {
      breakpoint: 1200,
      settings: { infinite: false, slidesToShow: toShow, slidesToScroll: 1 },
    },
    {
      breakpoint: 1024,
      settings: { infinite: false, slidesToShow: 3, slidesToScroll: 1 },
    },
    { breakpoint: 991, settings: { slidesToShow: 2, slidesToScroll: 1 } },
    { breakpoint: 750, settings: { slidesToShow: 2, slidesToScroll: 1 } },
    { breakpoint: 500, settings: { slidesToShow: 1, slidesToScroll: 1 } },
  ],
});

type Props = {
  title: string,
  seeAllLink: string,
  endpoint: string,
  showNoOfCards: number,
  Card: React.ReactElement,
  data? : Array<Object>,
  sortFunctions?: null | Array<() => void>,
  filterFunctions?: null | Array<() => void>,
};

const cached = new Map();

CardsSlider.defaultProps = {
  showNoOfCards: 4,
  sortFunctions: null,
  filterFunctions: null,
};

function CardsSlider(props: Props) {
  const { endpoint, title, Card, showNoOfCards, sortFunctions, filterFunctions, seeAllLink, data } = props;

  const [cards, setCards] = useState(cached.get(endpoint));
  
  React.useEffect(() => {
    if (cards && cards.length >= 0) return;

    if (data) {
      setCards(camelizeKeys(data));
      cached.set(endpoint, data);
    } else {
      api.get(endpoint).then(({ data }) => {
        setCards(camelizeKeys(data));
        cached.set(endpoint, data);
      });
    }
  }, [endpoint, cards, data]);

  const hasCardsToRender = cards && cards.length > 0;
  const filteredCards = (hasCardsToRender && filterFunctions) ? filter(cards, filterFunctions) : cards;
  const sortedCards = (hasCardsToRender && sortFunctions) ? sortBy(filteredCards, sortFunctions) : filteredCards;

  return (
    <WhitePanelLayout title={title} seeAllLink={seeAllLink}>
      <Slider {...settings(showNoOfCards)} className="featured-slider">
        {hasCardsToRender
          ? sortedCards.map(cardData => (
            <div key={cardData.id} className="item">
              <Card cardData={cardData} />
            </div>
          ))
          : Array.from({ length: showNoOfCards }, id => (
            <div key={id + 1} className="item">
              <SkeletonCard />
            </div>
          ))}
      </Slider>
    </WhitePanelLayout>
  );
};

export default memo(CardsSlider);
